import { useState, useEffect, ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Clock, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";

interface Props {
  children: ReactNode;
}

export const PartnerProtectedRoute = ({ children }: Props) => {
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);
  const [partnerStatus, setPartnerStatus] = useState<string | null>(null);

  useEffect(() => {
    const check = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setLoading(false);
        return;
      }

      setAuthenticated(true);

      const { data: partner } = await supabase
        .from("referral_partners")
        .select("status")
        .eq("user_id", session.user.id)
        .single();

      setPartnerStatus(partner?.status || null);
      setLoading(false);
    };

    check();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => {
      check();
    });

    return () => subscription.unsubscribe();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!authenticated) {
    return <Navigate to="/partner/login" replace />;
  }

  if (!partnerStatus) {
    return (
      <div className="min-h-screen bg-muted flex items-center justify-center p-4">
        <div className="max-w-md text-center bg-card rounded-xl shadow-lg border border-border p-8">
          <Building2 className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-xl font-bold text-foreground mb-2">Finish Your Partner Application</h2>
          <p className="text-muted-foreground mb-6">
            Your login works, but we don't have a completed partner profile for this email yet. Finish
            your application to access the portal — it only takes a minute.
          </p>
          <div className="flex flex-col gap-3">
            <Button onClick={() => window.location.href = "/partner/complete-application"}>Complete Application</Button>
            <Button variant="outline" onClick={async () => { await supabase.auth.signOut(); window.location.href = "/partner/login"; }}>
              Sign Out
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (partnerStatus === "suspended" || partnerStatus === "terminated") {
    return (
      <div className="min-h-screen bg-muted flex items-center justify-center p-4">
        <div className="max-w-md text-center bg-card rounded-xl shadow-lg border border-border p-8">
          <h2 className="text-xl font-bold text-foreground mb-2">Account {partnerStatus === "suspended" ? "Suspended" : "Terminated"}</h2>
          <p className="text-muted-foreground mb-6">
            Please contact us at (630) 686-9981 for assistance.
          </p>
          <Button variant="outline" onClick={async () => { await supabase.auth.signOut(); window.location.href = "/partner/login"; }}>
            Sign Out
          </Button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};
